/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import PathBrowserify from 'path-browserify';

/**
 * @import { Serializable } from '../../serializable'
 * @import { Stringable } from '../../stringable'
 * @import { FileSystemName } from '.'
 */

/**
 * Cesta v souborovém systému.
 *
 * Je to řetězec, který jednoznačně identifikuje jakoukoliv `FileSystemEntry` v celém souborovém systému.
 *
 * Maximální délka cesty u které je garantovaná bezproblémová funkcionalita je 255 znaků.
 *
 * Cesta je tvořena hierarchií `FileSystemName` oddělených znakem `/`.
 *
 * ![Diagram](./core/file/system/file-system-path.doc.png)
 *
 * Předchozí diagram znázorňuje strukturu absolutní cesty, kde:
 *
 * - Unit je název logické jednotky.
 * - Parents jsou názvy složek, do kterých je zanořený základní záznam.
 * - Base je základní záznam (složka nebo soubor), na který cesta odkazuje.
 *
 * Ukázka možných názvů logických jednotek: `data`, `install`, `temp`, `sdcard`, `usb`, `usb.2`.
 * Obecně jejich přesnou hodnotu určuje platforma, takže se mohou vzájemně lišit dle implementace.
 *
 * Ukázka možných cest:
 *
 * - `/internal` je hlavní složka jednotky `internal`
 * - `/internal/crocodile.txt` je soubor `crocodile.txt` na hlavní úrovni jednotky `internal`
 * - `/internal/dogs` je složka `dogs` na hlavní úrovni jednotky `internal`
 * - `/usb/cats/bengal.txt` je soubor `bengal.txt` ve složce `cats` na jednotce `usb`
 *
 * Implementace je založená na Node.js Path rozhraní v POSIX variantě.
 * Proto je snaha se od něj co nejméně lišit. Přesto zde jsou některé výjimky:
 *
 * - Parents nahrazují dir. Neobsahují root. Místo řetězce s odělovači vrací pole řetězců.
 * - První úroveň absolutní cesty se vrací jako unit.
 * - První úroveň absolutní cesty není zahrnutá do parents.
 * - Automatická normalizace cesty. Kvůli problémům s určením unit bez normalizace.
 *   Myšlenka je taková, že první úroveň absolutní path určuje unit.
 *   Jenže co u cest jako: `/./foo` nebo `/bar/../foo`? Tam se očekává výsledek `foo`.
 *   Tím pádem není vhodné pro určování jednotek používat nenormalizované cesty.
 * - Součástí přípony není tečka.
 *
 * U cest je možné používat ukončení lomítkem navíc, ale není to doporučeno ani testováno.
 * Podpora je zachována kvůli kompatibilitě s Node.js.
 *
 * Dokumentace:
 * [Cordova](https://cordova.apache.org/docs/en/latest/reference/cordova-plugin-file/index.html#cdvfile-protocol),
 * [Entries API](https://wicg.github.io/entries-api/#names-paths),
 * [Node.js](https://nodejs.org/dist/latest-v10.x/docs/api/path.html),
 * [Tizen](http://www.samsungdforum.com/B2B/Guide/APIReference/SSSP4/tizen971/index.html),
 * [webOS](https://webossignage-docs.developer.lge.com/guides/developer-guides/file-and-path/handling-file-paths).
 * @implements {Serializable}
 * @implements {Stringable}
 */
class FileSystemPath {
  /**
   * Vytvoří instanci.
   * @param {string} value Cesta jako řetězec.
   */
  constructor(value) {
    const normalized = PathBrowserify.normalize(value);
    this.normalized = normalized;
    this.pathObject = PathBrowserify.parse(normalized);
    this.value = value;
  }

  /**
   * Zjistí název záznamu, na který cesta odkazuje.
   *
   * Pojmenování vychází z [Node.js a POSIX basename](https://nodejs.org/docs/latest-v10.x/api/path.html#path_path_basename_path_ext).
   * @returns {FileSystemName} Název záznamu.
   */
  getBase() {
    if (this.isRoot()) {
      return '';
    }
    return this.pathObject.base;
  }

  /**
   * Zjistí jednotlivé segmenty, ze kterých se skládá cesta.
   * @returns {FileSystemName[]} Seznam názvů jednotlivých segmentů.
   */
  getSegments() {
    const { pathObject } = this;
    const dirString = pathObject.dir.slice(pathObject.root.length);
    const segments = [];
    if (dirString) {
      segments.push(...dirString.split('/'));
    }
    const { base } = pathObject;
    if (base) {
      segments.push(base);
    }
    return segments;
  }

  /**
   * Zjistí příponu záznamu, na který cesta odkazuje.
   *
   * Přípona neobsahuje tečku.
   * @returns {FileSystemName} Přípona záznamu.
   */
  getExtension() {
    if (this.isRoot()) {
      return '';
    }
    return this.pathObject.ext.slice(1);
  }

  /**
   * Zjistí normalizovanou cestu jako řetězec.
   * @returns {string} Cesta.
   */
  getNormalized() {
    // TODO Přejmenovat na toNormalized nebo normalize.
    // - Pojmenování get* nedává smysl, když jde o vrácení celé cesty
    //   v určitém formátu, ne jen zjištění její části.
    return this.normalized;
  }

  /**
   * Zjistí seznam názvů rodičovských složek.
   * @returns {FileSystemName[]} Seznam názvů složek.
   */
  getParents() {
    const segments = this.getSegments();
    if (this.isAbsolute()) {
      return segments.slice(1, -1);
    }
    return segments.slice(0, -1);
  }

  /**
   * Zjistí název záznamu bez přípony, na který cesta odkazuje.
   *
   * Pojmenování vychází z C++, které jako jediné používá jedinečné pojmenování.
   * Někdy nazýváno "base name", což koliduje s Node.js (respektive POSIX) "basename".
   * Někdy nazýváno "root name", což opět koliduje s Node.js "root" (Windows jednotka).
   * V Node.js nazýváno jen "name", což zase koliduje s pojmenováním Entries API.
   * Navíc v podstatě všechny části path jsou nějaké "name".
   * @returns {FileSystemName} Název záznamu bez přípony.
   */
  getStem() {
    if (this.isRoot()) {
      return '';
    }
    const { ext, name } = this.pathObject;
    if (ext.length === 1) {
      // Pokud je ext tvořena jen jedním znakem (tečkou), tak se kompenzuje její odebrání z extension.
      // Díky tomu lze libovolně převádět stem a extension na base a zpět.
      return name + ext;
    }
    return name;
  }

  /**
   * Zjistí název jednotky, na kterou cesta odkazuje.
   * @returns {FileSystemName} Název jednotky.
   */
  getUnit() {
    const segments = this.getSegments();
    if (this.isAbsolute() && segments.length) {
      return segments[0];
    }
    return '';
  }

  /**
   * Zjistí cestu jako řetězec v původní podobě bez normalizace.
   * @returns {string} Cesta.
   */
  getValue() {
    // TODO Přesunout do toString a normalizovanou cestu vracet jen v getNormalized.
    // - Stejně to funguje v ostatních API (Date, URL).
    // - Pojmenování get* nedává smysl, když jde o vrácení celé cesty
    //   v určitém formátu, ne jen zjištění její části.
    return this.value;
  }

  /**
   * Zjistí, jestli je cesta absolutní.
   * @returns {boolean} Příznak.
   */
  isAbsolute() {
    return PathBrowserify.isAbsolute(this.normalized);
  }

  /**
   * Zjistí, jestli cesta odkazuje na kořenovou složku.
   * @returns {boolean} Příznak.
   */
  isRoot() {
    return this.isAbsolute() && this.getSegments().length < 2;
  }

  /** @type {Serializable['toJSON']} */
  toJSON() {
    // Při exportu dat do JSON se používá originální hodnota řetězce s cestou.
    // Tak v případě změn v implementaci půjde zpracovat jinak.
    return this.getValue();
  }

  /** @type {Stringable['toString']} */
  toString() {
    // Při exportu do řetězce se použije normalizovaná cesta.
    // Ta je kratší a srozumitelnější pro člověka.
    return this.getNormalized();
  }
}

export default FileSystemPath;
